import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface MembershipSummary {
  role: string;
  permissions: string[];
}

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private membership$ = new BehaviorSubject<MembershipSummary | null>(null);

  /**
   * Update the current user's membership data.
   * Called by WorkspaceService when workspace data is loaded or switched.
   */
  setMembership(membership: MembershipSummary | null): void {
    this.membership$.next(membership);
  }

  /**
   * Get the current role as observable.
   */
  get currentRole$(): Observable<string | null> {
    return this.membership$.pipe(map((m) => m?.role ?? null));
  }

  /**
   * Get the current role synchronously.
   */
  get currentRole(): string | null {
    return this.membership$.value?.role ?? null;
  }

  /**
   * Get current permissions synchronously.
   */
  get permissions(): string[] {
    return this.membership$.value?.permissions ?? [];
  }

  /**
   * Check if the current user has a specific permission.
   */
  hasPermission(key: string): boolean {
    return this.permissions.includes(key);
  }

  /**
   * Check if the current user has any of the given permissions.
   */
  hasAnyPermission(keys: string[]): boolean {
    const perms = this.permissions;
    return keys.some((k) => perms.includes(k));
  }

  /**
   * Observable that emits whether the user has a specific permission.
   */
  hasPermission$(key: string): Observable<boolean> {
    return this.membership$.pipe(map((m) => m?.permissions?.includes(key) ?? false));
  }

  /**
   * Observable that emits whether the user has at least one of the given
   * permissions. Useful for write controls covered by more than one key
   * (e.g. `datasources.create` OR `datasources.edit`).
   */
  hasAnyPermission$(keys: string[]): Observable<boolean> {
    return this.membership$.pipe(
      map((m) => keys.some((k) => m?.permissions?.includes(k) ?? false))
    );
  }

  /**
   * Clear membership data (e.g., on logout or workspace clear).
   */
  clear(): void {
    this.membership$.next(null);
  }
}
